import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../components/Headings";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import PageLayout from "../../components/PageLayout/PageLayout";
import PhoneLayout from "../../components/PhoneLayout/PhoneLayout";
import { PokeButton } from "../../components/PokeButton/PokeButton";
import { useAppAuthState } from "../../hooks/useAppAuthState";
import { usePublicUserProfile } from "../../hooks/usePublicUserProfile";
import { useUserProfile } from "../../hooks/useUserProfile";
import { SitePage } from "../../types/SitePage";
import "./PlayerPage.scss";
import { PublicSocialLink } from "./PublicSocialLink";

const PlaceholderImage = "https://placehold.co/250x250";

const PlayerPage: React.FC = () => {
  const showPokeButton = false;

  const { playerUsername } = useParams<{ playerUsername: string }>();
  const { user: firebaseUser, isAuthLoading } = useAppAuthState();
  const {
    profile: viewedUserProfile,
    loading: viewedUserProfileLoading,
    error: viewedUserProfileError,
  } = usePublicUserProfile(playerUsername);

  const {
    profile: currentUserProfile,
    loading: currentUserProfileLoading,
    error: currentUserProfileError,
  } = useUserProfile(firebaseUser?.uid);

  const pageContent = useMemo(() => {
    if (!firebaseUser) {
      return (
        <ErrorDisplay
          error={new Error("You must be logged in to view player profiles.")}
        />
      );
    }

    if (viewedUserProfileLoading || currentUserProfileLoading) {
      return null;
    }

    if (viewedUserProfileError) {
      return <ErrorDisplay error={viewedUserProfileError} />;
    }

    if (currentUserProfileError && firebaseUser) {
      return (
        <ErrorDisplay
          error={currentUserProfileError}
          message="Could not load your profile information."
        />
      );
    }

    if (!viewedUserProfile) {
      return <ErrorDisplay error={new Error("Player profile not found.")} />;
    }

    return (
      <>
        <div className="profile-image-container">
          <img
            src={viewedUserProfile?.photoURL || PlaceholderImage}
            alt="Profile"
            className="profile-image"
          />
        </div>
        <Headings.PhoneMain className="player-handle">
          {viewedUserProfile?.username}
        </Headings.PhoneMain>
        {showPokeButton && (
          <PokeButton
            onPokeSuccess={() => {}}
            currentUserProfile={currentUserProfile}
            recipientUserProfile={viewedUserProfile}
          />
        )}

        {firebaseUser && firebaseUser.uid === viewedUserProfile.userId && (
          <p className="mt-3 text-muted">This is your public profile.</p>
        )}
        <div className="socials-container">
          {viewedUserProfile.socials?.map((social, i) => (
            <PublicSocialLink key={`${social.type}-${i}`} social={social} />
          ))}
        </div>
      </>
    );
  }, [
    showPokeButton,
    viewedUserProfileLoading,
    currentUserProfileLoading,
    viewedUserProfileError,
    currentUserProfileError,
    firebaseUser,
    viewedUserProfile,
    currentUserProfile,
  ]);

  if (
    isAuthLoading ||
    viewedUserProfileLoading ||
    (firebaseUser && currentUserProfileLoading)
  ) {
    return <LoadingMask message="Loading player profile..." />;
  }

  return (
    <PageLayout page={SitePage.Profile} title={playerUsername || "Player"}>
      <PhoneLayout>
        <div className="player-page-container">{pageContent}</div>
      </PhoneLayout>
    </PageLayout>
  );
};

export default PlayerPage;
